import type {
    GetRoleDistributionsResponseBody,
    GetRolesResponseBody,
    GetServiceOrganizationSettingRoleDistributionsQuery,
    UpdateRoleDistributionsRequestBody,
    UpdateRolesRequestBody,
} from '@common/interfaces/api/serviceOrganizationSettings';
import type {
    AssetValidation,
    IServiceOrganizationSettingAddEditValidated,
} from '@common/validation/serviceOrganizationSetting';
import { ServiceCode } from '@common/enums/serviceCode';
import { getUrlQueryParameters } from '@frontend/services/api/queryParameterUtils';
import { fetchJson } from '@frontend/store/fetch';

const baseUrl = '/api/serviceOrganizationSettings';

export async function updateServiceOrganizationSetting(
    id: string,
    body: IServiceOrganizationSettingAddEditValidated,
    signal: AbortSignal
): Promise<void> {
    const url = `${baseUrl}/${id}`;

    await fetchJson({ body, method: 'POST', signal, url });
}

export async function getRoleDistributions(
    serviceOrganizationSettingId: string,
    query: GetServiceOrganizationSettingRoleDistributionsQuery | undefined,
    signal: AbortSignal
): Promise<GetRoleDistributionsResponseBody> {
    const url = getUrlQueryParameters<GetServiceOrganizationSettingRoleDistributionsQuery>(
        `${baseUrl}/${serviceOrganizationSettingId}/roleDistributions`,
        query
    );

    const result = await fetchJson({ method: 'GET', signal, url });

    return result.json;
}

export async function getRoles(
    serviceOrganizationSettingId: string,
    signal: AbortSignal
): Promise<GetRolesResponseBody> {
    const url = `${baseUrl}/${serviceOrganizationSettingId}/roles`;
    const result = await fetchJson({ method: 'GET', signal, url });

    return result.json;
}

export async function updateRoleDistributions(
    serviceOrganizationSettingId: string,
    body: UpdateRoleDistributionsRequestBody,
    signal: AbortSignal
): Promise<void> {
    const url = `${baseUrl}/${serviceOrganizationSettingId}/roleDistributions`;

    await fetchJson({ body, method: 'PUT', signal, url });
}

export async function updateRoles(
    serviceOrganizationSettingId: string,
    body: UpdateRolesRequestBody,
    signal: AbortSignal
): Promise<void> {
    const url = `${baseUrl}/${serviceOrganizationSettingId}/roles`;

    await fetchJson({ body, method: 'PUT', signal, url });
}

export async function getOne(id: string, signal: AbortSignal) {
    const url = `${baseUrl}/${id}`;

    return await fetchJson({ method: 'GET', signal, url });
}

export async function getAssetValidationSettings(organizationId: string): Promise<AssetValidation | undefined> {
    const url = `${baseUrl}?serviceCode=${ServiceCode.TFO}&organizationId=${organizationId}&isActive=true`;
    const result = await fetchJson({ method: 'GET', url });

    return result.json.items[0]?.assetValidation;
}
